import { Box, Typography } from "@mui/material";
import { get } from "lodash";
import React, { FC, useMemo } from "react";
import SummaryLine from "src/components/ui-component/form-component/abschluss-page/SummaryLine";
import { useApplication } from "src/context/ApplicationContext";
import { getLocalstorageToken } from "src/context/AuthHelper";
import {
  KOSTEN_ARTEN,
  KOSTEN_POSITION,
  KOSTENTRAEGER,
} from "src/lib/constants/constant";
import MittelabrufSchema from "src/lib/paths/mittelabruf_FULL_Paths.json";
import KostenPositionSchema from "src/lib/paths/kostenposition_FULL_Paths.json";
import ApplicationHeading from "src/pages/private/online-antrag/application-heading/ApplicationHeading";
import { getVerbundPartnerName } from "src/pages/private/online-antrag/application-helper-function/AppHelperfunctions";
import { DisplayDecimal, DisplayEuro } from "src/utils/formats";
import { TreeNodeType } from "src/lib/types/types";
import { getLoeweMittelabrufOrganizations } from "./LoeweMittelabrufOrganizations";

const MA_P = MittelabrufSchema.attributes;
const KP_P = KostenPositionSchema.attributes;

type YearSummary = {
  year: number;
  eligibleExpenses: number;
  personnelExpenses: number;
  materialExpenses: number;
  privateFunds: number;
  fundingRate: number;
  approvedFunding: number;
};

type PartnerSummary = {
  id: number | string;
  label: string;
  rows: YearSummary[];
};

const toNumber = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const getYear = (value: unknown): number | null => {
  const year = Number(value);
  return Number.isInteger(year) && year > 0 ? year : null;
};

const getYearValue = (rows: any[], year: number, field: string): number => {
  const row = rows.find((entry) => getYear(get(entry, "Projektjahr")) === year);
  return toNumber(get(row, field, 0));
};

const getFundingRate = (rows: any[], year: number): number =>
  getYearValue(rows, year, "Foerderquote_403741");

const getAnnualCost = (costPosition: any, year: number): number => {
  const annualCost = get(
    costPosition,
    KP_P.Allgemein_Kosten_Pro_Jahr.path,
    [],
  ).find((entry: any) => getYear(get(entry, "Zelle_1")) === year);
  return toNumber(get(annualCost, "Zelle_2", 0));
};

const getYears = (
  application: any,
  costPositions: any[],
  approvalRows: any[],
): number[] => {
  const startYear = getYear(
    get(
      application,
      MA_P.Foerderantrag.schema.attributes.Allgemein_Projektstart_Jahr.path,
    ),
  );
  const duration = Math.max(
    0,
    Math.floor(
      toNumber(
        get(
          application,
          MA_P.Foerderantrag.schema.attributes.Allgemein_Projektdauerin_Jahren
            .path,
        ),
      ),
    ),
  );
  const years = new Set<number>();

  if (startYear && duration > 0) {
    for (let index = 0; index < duration; index++) years.add(startYear + index);
  }

  for (const row of approvalRows) {
    const year = getYear(get(row, "Projektjahr"));
    if (year) years.add(year);
  }
  for (const costPosition of costPositions) {
    for (const row of get(
      costPosition,
      KP_P.Allgemein_Kosten_Pro_Jahr.path,
      [],
    )) {
      const year = getYear(get(row, "Zelle_1"));
      if (year) years.add(year);
    }
  }

  return Array.from(years).sort((a, b) => a - b);
};

const getOrganizationCostPositions = (
  costPositions: any[],
  organizationId: number | string,
): any[] =>
  costPositions.filter(
    (costPosition) =>
      get(costPosition, `${KP_P.Unternehmen.path}.id`) === organizationId,
  );

export const summarizeOrganization = (
  costPositions: any[],
  organizationId: number | string,
  years: number[],
  fundingRows: any[],
): YearSummary[] =>
  years.map((year) => {
    let personnelExpenses = 0;
    let materialExpenses = 0;
    let privateFunds = 0;

    for (const costPosition of getOrganizationCostPositions(
      costPositions,
      organizationId,
    )) {
      const amount = getAnnualCost(costPosition, year);
      const position = get(costPosition, KP_P.Allgemein_Kostenposition.path);
      const fundingType = get(
        costPosition,
        KP_P.Allgemein_Kostenart_Finanzierung.path,
      );

      if (
        position === KOSTEN_POSITION.Ausgabe &&
        fundingType === KOSTEN_ARTEN.Personalkosten
      )
        personnelExpenses += amount;
      if (
        position === KOSTEN_POSITION.Ausgabe &&
        fundingType === KOSTEN_ARTEN.Sachkosten
      )
        materialExpenses += amount;
      if (
        position === KOSTEN_POSITION.Einnahme &&
        fundingType === KOSTEN_ARTEN.Eigenmittel
      )
        privateFunds += amount;
    }

    const eligibleExpenses = personnelExpenses + materialExpenses;
    const fundingRate = getFundingRate(fundingRows, year);

    return {
      year,
      eligibleExpenses,
      personnelExpenses,
      materialExpenses,
      privateFunds,
      fundingRate,
      approvedFunding: eligibleExpenses - privateFunds,
    };
  });

const getApplicantOrganizationId = (
  costPositions: any[],
): number | string | null => {
  const applicantCostPosition = costPositions.find(
    (costPosition) =>
      get(costPosition, KP_P.Allgemein_Kostentraeger.path) ===
      KOSTENTRAEGER.Antragsteller,
  );
  return get(applicantCostPosition, `${KP_P.Unternehmen.path}.id`, null);
};

const getOverallRows = (
  approvalRows: any[],
  organizationRows: PartnerSummary[],
  years: number[],
): YearSummary[] =>
  years.map((year) => {
    const approvalRow = approvalRows.find(
      (row) => getYear(get(row, "Projektjahr")) === year,
    );
    const organizationTotals = organizationRows.reduce(
      (total, organization) => {
        const row = organization.rows.find((entry) => entry.year === year);
        if (!row) return total;
        return {
          eligibleExpenses: total.eligibleExpenses + row.eligibleExpenses,
          personnelExpenses: total.personnelExpenses + row.personnelExpenses,
          materialExpenses: total.materialExpenses + row.materialExpenses,
          privateFunds: total.privateFunds + row.privateFunds,
        };
      },
      {
        eligibleExpenses: 0,
        personnelExpenses: 0,
        materialExpenses: 0,
        privateFunds: 0,
      },
    );
    const eligibleExpenses = toNumber(
      get(approvalRow, "Ausgaben", organizationTotals.eligibleExpenses),
    );
    const privateFunds = toNumber(
      get(
        approvalRow,
        "Eigen_45_Und_Drittmittel",
        organizationTotals.privateFunds,
      ),
    );
    const approvedFunding = toNumber(
      get(approvalRow, "Zuwendung", eligibleExpenses - privateFunds),
    );
    const fundingRate = toNumber(
      get(
        approvalRow,
        "Foerderquote_403741",
        eligibleExpenses ? (approvedFunding / eligibleExpenses) * 100 : 0,
      ),
    );

    return {
      year,
      eligibleExpenses,
      personnelExpenses: organizationTotals.personnelExpenses,
      materialExpenses: organizationTotals.materialExpenses,
      privateFunds,
      fundingRate,
      approvedFunding,
    };
  });

type Props = {
  organizationId?: number | string;
  parentOnly?: boolean;
  hideHeading?: boolean;
};

const UebersichtBewilligungMALoewe: FC<Props> = ({
  organizationId,
  parentOnly = false,
  hideHeading = false,
}) => {
  const { application } = useApplication();

  const { summaries, availableFunding } = useMemo(() => {
    const costPositions = get(
      application,
      MA_P.Foerderantrag.schema.attributes.Foerderantrag_Kostenpositions.path,
      [],
    );
    const approvalRows = get(
      application,
      MA_P.Foerderantrag.schema.attributes.Bewilligung_Jahresaufteilung.path,
      [],
    );
    const years = getYears(application, costPositions, approvalRows);
    const applicantOrganizationId = getApplicantOrganizationId(costPositions);
    const applicantFundingRows = get(
      application,
      MA_P.Foerderantrag.schema.attributes
        .Antragstellende_Organisation_Foerderquote.path,
      [],
    );
    const applicantName = get(
      application,
      MA_P.Foerderantrag.schema.attributes
        .Antragstellende_Organisa_0074063902_licher_Name_Der_Organisation.path,
      "Antragsteller",
    );
    const organizations: PartnerSummary[] = [];

    if (applicantOrganizationId !== null) {
      organizations.push({
        id: applicantOrganizationId,
        label: applicantName,
        rows: summarizeOrganization(
          costPositions,
          applicantOrganizationId,
          years,
          applicantFundingRows,
        ),
      });
    }

    const partners = get(
      application,
      MA_P.Foerderantrag.schema.attributes.Verbundpartner.path,
      [],
    );
    partners.forEach((partner: any, index: number) => {
      const organizationId = get(partner, "Verbundpartner.id");
      if (organizationId === undefined || organizationId === null) return;
      organizations.push({
        id: organizationId,
        label: getVerbundPartnerName(partner, index),
        rows: summarizeOrganization(
          costPositions,
          organizationId,
          years,
          get(partner, "Foerderquote", []),
        ),
      });
    });

    const total: PartnerSummary = {
      id: "gesamt",
      label: "Gesamt",
      rows: getOverallRows(approvalRows, organizations, years),
    };

    return {
      summaries: [...organizations, total],
      availableFunding: Math.max(
        0,
        toNumber(
          get(
            application,
            MA_P.Foerderantrag.schema.attributes
              .Bewilligung_Bewilligte_Zuwendung.path,
            0,
          ),
        ) -
          toNumber(
            get(
              application,
              MA_P
                .Ermittlung_Des_Auszahlun_0254424978_ngen_Im_Gleichen_Projektjahr
                .path,
              0,
            ),
          ),
      ),
    };
  }, [application]);

  const selectedSummary =
    organizationId !== undefined
      ? summaries.find(
          (summary) => String(summary.id) === String(organizationId),
        )
      : summaries[summaries.length - 1];

  if (parentOnly) {
    return (
      <>
        <ApplicationHeading title="Übersicht Bewilligung" />
        <Typography>
          Folgende Projektdaten sind lt. Zuwendungsvertrag bewilligt und zu
          berücksichtigen:
        </Typography>
      </>
    );
  }

  return (
    <>
      {!hideHeading && <ApplicationHeading title="Übersicht Bewilligung" />}
      {!hideHeading && (
        <Typography>
          Folgende Projektdaten sind lt. Zuwendungsvertrag bewilligt und zu
          berücksichtigen:
        </Typography>
      )}

      {selectedSummary && (
        <Box sx={{ mt: "1.5rem" }}>
          {selectedSummary.rows.map((row) => (
            <Box key={row.year} sx={{ mb: "1.5rem" }}>
              <Typography sx={{ fontWeight: 600, mb: "0.25rem" }}>
                Projektjahr {row.year}
              </Typography>
              <SummaryLine
                label="Zuwendungsfähige Gesamtausgaben bis zu:"
                alignValueRight
                boldLabel
                value={DisplayEuro(row.eligibleExpenses)}
              />
              <SummaryLine
                label="davon Personalausgaben bis zu:"
                labelPadding="0.75rem 2rem"
                alignValueRight
                value={DisplayEuro(row.personnelExpenses)}
                valueFontWeight={400}
              />
              <SummaryLine
                label="davon Sachausgaben bis zu:"
                labelPadding="0.75rem 2rem"
                alignValueRight
                value={DisplayEuro(row.materialExpenses)}
                valueFontWeight={400}
              />
              <SummaryLine
                label="Eigenmittel gesamt:"
                alignValueRight
                value={DisplayEuro(row.privateFunds)}
                valueFontWeight={400}
              />
              <Box sx={{ mt: "1.5rem" }}>
                <SummaryLine
                  label="Förderquote:"
                  alignValueRight
                  boldLabel
                  value={DisplayDecimal(row.fundingRate, true)}
                />
                <SummaryLine
                  label="Bewilligte Förderung:"
                  alignValueRight
                  boldLabel
                  value={DisplayEuro(row.approvedFunding)}
                />
              </Box>
            </Box>
          ))}
          {selectedSummary.id === "gesamt" && (
            <Box sx={{ mt: "1.5rem" }}>
              <SummaryLine
                label="Noch verfügbare Fördermittel:"
                alignValueRight
                value={DisplayEuro(availableFunding)}
                valueFontWeight={400}
              />
              <Typography sx={{ mt: "2rem" }}>
                Bitte beachten Sie die Informationen zur Erstellung des
                Mittelabrufs im Menüpunkt „Wichtige Hinweise“.
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </>
  );
};

export const getUebersichtBewilligungMALoeweChildren = (
  application: any,
): TreeNodeType[] => {
  const applicantOrganizationId = get(getLocalstorageToken(), "organisationId");
  const organizations = getLoeweMittelabrufOrganizations(
    application,
    applicantOrganizationId,
  );
  const organizationTabs = [
    ...organizations,
    { id: "gesamt", label: "Gesamt", kostentraeger: "" },
  ];

  return organizationTabs.map((organization) => ({
    label: organization.label,
    content: (
      <UebersichtBewilligungMALoewe
        organizationId={organization.id}
        hideHeading
      />
    ),
    showError: false,
  }));
};

export default UebersichtBewilligungMALoewe;
